import { apiClient } from '../common/apiClient';
import { PendingMemo, AcceptedMemo } from '../types/app';

/**
 * Fetch pending company memos for the logged-in user
 * @returns Array of pending memos with name, subject and fromName
 */
export async function fetchPendingMemos(): Promise<{ name: string; subject: string; fromName: string }[]> {
  try {
    const response = await apiClient.get<{ name: string; subject: string; fromName: string }[]>('/api/salesforce/memos/pending-approvals');
    return response || [];
  } catch (error) {
    console.error('Failed to fetch pending memos:', error);
    return [];
  }
}

/**
 * Fetch pending company memos for a user by email
 * @param email The user's email address
 * @returns Array of pending memos with name, subject and fromName
 */
export async function fetchPendingMemosByEmail(email: string): Promise<PendingMemo[]> {
  try {
    const response = await apiClient.get<PendingMemo[]>(`/api/salesforce/memos/pending-approvals?email=${encodeURIComponent(email)}`);
    return response || [];
  } catch (error) {
    console.error('Failed to fetch pending memos by email:', error);
    return [];
  }
}

/**
 * Fetch accepted/previous company memos for a user by email
 * @param email The user's email address
 * @returns Array of accepted memos with name, subject, fromName, and acceptedDate
 */
export async function fetchAcceptedMemosByEmail(email: string): Promise<AcceptedMemo[]> {
  try {
    const response = await apiClient.get<AcceptedMemo[]>(`/api/salesforce/memos/accepted?email=${encodeURIComponent(email)}`);
    return response || [];
  } catch (error) {
    console.error('Failed to fetch accepted memos by email:', error);
    return [];
  }
}

// ============== MEMO ADMIN FUNCTIONS ==============

export interface CompanyMemo {
  Id: string;
  Name: string;
  Memo_Subject__c: string;
  From_Display_Text__c: string;
  CreatedDate: string;
  Publish_Date__c?: string;
  Memo_Number__c?: string;
  Is_Draft__c?: boolean;
  Is_Archived__c?: boolean;
  source?: string;
}

export async function checkIsMemoAdmin(email: string): Promise<{ isAdmin: boolean }> {
  try {
    const response = await apiClient.get<{ isAdmin: boolean }>(`/api/salesforce/memos/admin/check?email=${encodeURIComponent(email)}`);
    return response;
  } catch (error) {
    console.error('Failed to check memo admin status:', error);
    return { isAdmin: false };
  }
}

interface AllMemosResponse {
  salesforce: CompanyMemo[];
  uploaded: CompanyMemo[];
  total: number;
  fetchedAt: string;
}

export async function fetchAllCompanyMemos(): Promise<CompanyMemo[]> {
  try {
    const response = await apiClient.get<AllMemosResponse>('/api/salesforce/memos/all');
    // Combine salesforce and uploaded memos into one array
    const allMemos = [...(response.salesforce || []), ...(response.uploaded || [])];
    return allMemos;
  } catch (error) {
    console.error('Failed to fetch all company memos:', error);
    return [];
  }
}
